// Grading and knowledge: the stored policy's own lifecycle, the CI jobs and logs
// it gates, the CodeRabbit drop-in, our review verdicts and takeovers, the babysit
// transitions that carry a PR to merge — and the knowledge the fleet accumulates
// while doing it (integrations, KB pages, imported backlog, provider
// disagreements, fleet credentials and alerts).
// Split out of `../audit.ts`, which is now the barrel over this directory. Not in
// the barrel: this is a HALF of `AuditEventKind`, exported only so `./kind` can
// assemble it. Import `AuditEventKind` — never a group.

export const AUDIT_KIND_REVIEW = {
  // #101 slice 04: a stored policy failed shape/semantic validation at load.
  // The loader logged the error; this audit row documents the fallback to
  // defaultPolicy, mirroring AccountPaused (a silent fallback would be untraceable).
  PolicyValidationFailed: 'policy.validation_failed',
  PolicyActivated: 'policy.activated',
  PolicyDeleted: 'policy.deleted',
  PolicyStaleRejected: 'policy.stale_rejected',
  // #101 slice 04: CI integration audit kinds.
  CiJobNoCapacity: 'ci.job.no_capacity',
  CiPermissionMissing: 'ci.permission_missing',
  CiLogRecorded: 'ci.log.recorded',
  CiLogUploadFailed: 'ci.log.upload_failed',
  // arc 101 slice 07 PR 22: the CI-log DB prune lane — the platform's 72h/3-day
  // retention (mirrors `LogRetentionPruned`, but for the `ci_jobs.log_ref`
  // object pointer rather than `task_run_events` payloads). Sweeping the R2
  // object + nulling the row's pointer is a state change (it's what makes the
  // presign/API surface start 404ing), so every pass — success AND failure —
  // gets a row. Parent-less (cron lane), emitted through the worker's
  // structured job-audit log.
  CiLogPruned: 'ci.log.pruned',
  CiPolicyDecision: 'ci.policy.decision',
  // The account's cached GitHub runner-group id was forgotten because GitHub answered 404
  // for it (the group was deleted on their side). The next CI placement re-resolves the
  // group, so without this row the only trace of the change was a cache row that is gone.
  CiRunnerGroupForgotten: 'ci.runner_group.forgotten',
  // The repository selection of the account's GitHub runner group was written (a PUT to
  // GitHub): which repositories may run their CI jobs on that group's runners.
  CiRunnerGroupSelectionSet: 'ci.runner_group.selection_set',
  InternalRepoSkipped: 'internal.repo.skipped',
  CoderabbitSnapshotPersisted: 'coderabbit.snapshot.persisted',
  CoderabbitProbeUpdated: 'coderabbit.probe.updated',
  CoderabbitEnqueueDecision: 'coderabbit.enqueue.decision',
  // #1264: the repo asked for `review.depth: deep` and the account has NO
  // enrolled server that could ever run it. `deep` is the one review setting
  // that needs a resource the customer brings — the build/test/lint pass runs on
  // an enrolled box holding a checkout, and nothing in the cluster can stand in
  // for it. The box-side disclosure cannot cover this case, because with no box
  // there is no review posted to disclose anything ON: this row is the entire
  // surface, so it is `visibleToMaintainer` (a human must act — enroll a box or
  // set `depth: inline`). Fired at the ENQUEUE hop, where the promise is made,
  // and only on a fresh mint, so one reviewed commit earns at most one row.
  // Session-less (webhook-driven, no agent ran) but NOT parent-less: since #2027
  // the row is DURABLE, chained onto the account's `ensureGatewaySessionParent`
  // anchor, like the rest of the review lane's account-anchored rows. It used to
  // ride the worker's stdout job-audit store, which is why prod held 460
  // `pr_review` tasks and ZERO rows of this kind — a `visibleToMaintainer` row
  // that never reaches `audit_events` is a promise to a UI that cannot be kept.
  ReviewDepthUnservable: 'review.depth_unservable',
  // #2321 (slice 12): the repo defers code review to a third-party bot, and we
  // reviewed anyway because that bot COULD NOT review this head — it declared a
  // quota outage, or it stayed silent past the grace window. Its own kind rather
  // than a `CoderabbitEnqueueDecision` with a new reason string, for two reasons
  // that outlive the string: the decision is now VENDOR-KEYED (CodeRabbit,
  // Greptile, whatever ships next), and the takeover RATE is the number that
  // says whether the deference config is wrong — a rate you can only compute if
  // takeovers are countable, which a reason buried in another kind's payload is
  // not. It is also the evidence epic #2200 needs to argue review parity later,
  // and it is un-reconstructable after the fact: nothing else records that an
  // incumbent was asked and did not answer.
  //
  // `visibleToMaintainer`: yes at the call site. A maintainer who chose
  // CodeRabbit is entitled to know we reviewed over the top of it, and the
  // posted review says so too (`renderTakeoverDisclosure`) — this row is the
  // durable half of the same disclosure.
  ReviewTakeover: 'review.takeover',
  // #2270: an operator / control-plane verb that was filed as `mcp_tool.call.*` with its
  // name in `tool`. The value IS that name, so a new row's `kind` equals the old rows'
  // `tool`. For a name that was NEVER split, one query reads both:
  // `kind = $1 OR (kind LIKE 'mcp_tool.call.%' AND tool = $1)`. A name #6124 split by
  // verdict (an ok/err pair, or `plan.file_refused`) has its own reads, beside it.
  // An operator re-ran a review (`deps/review-rerun.ts`).
  ReviewRerun: 'review.rerun',
  // tesote/tesote.com#240: the review gate's VERDICT for a head with nothing in it to
  // review — every changed path is excluded by the repo's `path_filters` or the built-in
  // junk list (a lockfile-only dependency bump). It is the stored record of the
  // `nothing_reviewable` verdict (`NOTHING_REVIEWABLE_VERDICT`): `input` pins
  // `repo`/`prNumber`/`headSha`, and `@developerz/db` `findLatestPlatformReviewVerdict`
  // reads it back for exactly that head, so `pr.require_review` counts the head as
  // reviewed instead of escalating `review_absent` on a review that can never come. Its
  // own kind, not a `policy.gate.refused` reason: it is read on the merge path through
  // `audit_events_kind_idx`, and `policy.gate.refused` carried ~19k rows in the 30 days
  // to 2026-10-07 (measured on prod before this kind existed). Durable, account-anchored,
  // `visibleToMaintainer`.
  ReviewNothingReviewable: 'review.nothing_reviewable',
  // #544 (epic #541): the mothership PR-babysit DRIVER graded one webhook into the
  // single next action toward merge (`@developerz/domain` `decideBabysit`) and drove
  // it — a state transition (advance the task's watch state, mint a fix/address
  // follow-up, merge the PR, or escalate to a human). Every hop earns a row: audit
  // is trust, and the babysit lifecycle is autonomous. Session-less (webhook-driven,
  // no agent ran) but NOT parent-less: since #2018 the row is DURABLE, chained onto
  // the account's `ensureGatewaySessionParent` anchor, because a consumer reads these
  // rows back out of `audit_events` (`countGithubRefusedForHead`) and for the whole
  // life of the lane before that they went only to a `console.log` — 110,905 audit
  // rows in prod, zero of this kind, so `github_refused_stuck` could never fire. The
  // one exception is the repo-activation stand-down (`event: 'repo_stand_down'`),
  // which resolves NO account by design and therefore has no parent to chain onto; it
  // stays on the structured-log belt. An `escalate` hop is `visibleToMaintainer`
  // (a human must act); a routine advance is not.
  BabysitTransition: 'babysit.transition',
  // #836: the worker's gh-dispatch consumer (the `gh.dispatch` lane) executed a
  // dispatched gh action — opened the disclosed GitHub issue the `gh` task-sink
  // delivers. An outward, maintainer-visible write on their repo, so it earns a
  // row (audit is trust) carrying the task id, repo, and the opened issue's
  // number + url. Parent-less (no `agent_sessions` row for a queue hop), so it
  // is emitted through the worker's structured job-audit store like the other
  // consumer lanes. Named to mirror the INBOUND `gh.issue.received` webhook
  // lane — this one is the OUTBOUND write.
  GhIssueOpened: 'gh.issue_opened',
  // Plan 102 slice 07 (#7553): CLOSE THE LOOP — every issue and PR reaches an end, and each
  // end the platform writes on GitHub is one row. Typed payloads, writers and reasons:
  // `./payload-close-loop.ts`. Every one is `visibleToMaintainer: true` (an outward write on
  // the maintainer's repository). Stored vocabulary, never renamed.
  // The orphaned-ask sweep (or a no-fix diagnosis) posted the final disclosed answer on an
  // issue and closed it `not planned`.
  IssueClosedFinal: 'issue.closed_final',
  // A diagnose run's report was posted on its issue (#7542), and what followed it.
  IssueDiagnosisPosted: 'issue.diagnosis_posted',
  // An escalated issue ask's outcome was posted on its issue, once (#7538, review F4).
  IssueEscalationPosted: 'issue.escalation_posted',
  // Babysit closed our own PR refused on gate paths and refiled its non-gate part (07 row c).
  PrSelfRefiled: 'pr.self_refiled',
  // Babysit closed our own PR a recovered default branch made moot (#7536).
  PrSupersededClosed: 'pr.superseded_closed',
  // Babysit graded an outside contributor's PR on its diff: the census the thresholds retune on.
  ExternalIntakeGraded: 'external_intake.graded',
  // The external ban (#7785 part 3), `./payload-external-ban.ts`: one paid judgement of an outsider's
  // text; an author joining or leaving a list; an item meeting a ban, a verdict or a limit; a third
  // unrelated witness promoting an author; a banned author's merged PR filed for a second look.
  ExternalDangerJudged: 'external_danger.judged',
  ExternalBanAdded: 'external_ban.added',
  ExternalBanRemoved: 'external_ban.removed',
  ExternalBanWriteRefused: 'external_ban.write_refused',
  ExternalBanEnforced: 'external_ban.enforced',
  ExternalBanPromoted: 'external_ban.promoted',
  ExternalBanRereviewFiled: 'external_ban.rereview_filed',
  // Plan 102 slice 07b (#7557): the platform deleted a branch it judged finished (a closed or
  // merged PR's head whose tip is still that head, or our own terminal task's `dz/` branch).
  BranchDeleted: 'branch.deleted',
  // #31 (arc 101 slice 01): the stuck-reconciliation sweep EXPIRED a side-effect
  // reservation wedged in `pending` — the reserving run died between `reserve`
  // and `complete`/`release`, so the effect's fate is unknown and the loop was
  // failing every later attempt closed (`in_flight_lost`). Deleting that claim
  // re-opens the key, i.e. it authorizes a re-attempt of an external side effect
  // that MAY already have landed — the single most consequential write in the
  // idempotency ledger, so it earns a row per key (never just a metric line).
  // Chained on the LANE, never parent-less: `cron.stuck_reconciliation` declares
  // the `platform` audit arm (`apps/worker/src/cron/stuck-reconciliation.lane.ts`),
  // so this row's home is ONE `audit_events` chain per lane —
  // `composePlatformAuditStore` over the worker's long-lived `dbAuditStore`,
  // anchored on `platformSessionParentId('stuck-reconciliation')` — and not the
  // stdout job-audit line a pod restart takes with it. `platform` and not
  // `tenant` because the sweep's cutoff is a clock over every `reconciliations`
  // row, so there is no one account to anchor on. The reconciled TOOL rides
  // `input`: `appendEvent` REFUSES a top-level `tool` beside a non-`tool.call.*`
  // kind, so the binding this replaces threw on every expiry it ever attempted.
  ReconciliationExpired: 'reconciliation.expired',
  // #750 (arc 101 slice 01): the stuck-session reaper finalized an ORPHANED
  // session — a run whose pod died (SIGKILL / OOM / node drain) after
  // `session.boot` but before its terminal row, leaving `agent_sessions`
  // non-terminal forever (the dashboard shows it "running" and the free-tier
  // session cap keeps counting it). Deliberately NOT `session.failed`: that kind
  // is the loop's own verdict about a cause it observed, whereas this one is an
  // external janitor's verdict from evidence of ABSENCE (no audit event for the
  // silence window, entity lock gone). Chained onto the reaped session's OWN
  // chain — unlike the other cron lanes this one has a real `agent_sessions`
  // parent, and the session's story should end inside its own trail.
  SessionReaped: 'session.reaped',
  // #750 re-drive: after the stuck-session reaper finalizes a crash-orphaned row,
  // it RE-ENQUEUES the original `agent.run` (the delivery that triggered the dead
  // run — a human comment otherwise lost with the pod), so the maintainer work
  // isn't silently dropped. One row per re-drive, appended to the reaped session's
  // OWN chain right after `session.reaped` — the janitor's note that the entity's
  // work was handed to a fresh session. Loud by design (also logged at alert
  // level): a re-drive means a pod died mid-run. The re-enqueue rides the stable
  // per-entity coalesce jobId, so a redrive racing a fresh delivery is a BullMQ
  // no-op, and the reconcile ledger stops any duplicate external write.
  SessionRedriven: 'session.redriven',
  // #260 (arc 101 slice 03): the mothership KB consumer applied a review
  // runner's `*_learnings` write-back — one `repo_memory` wiki page created or
  // REPLACED by (repo, slug). Unlike the append-only tables, a page write
  // overwrites the prior body, so the audit row is the only durable record that
  // run X rewrote page Y (and the only trace an untrusted box wrote into a
  // tenant's knowledge base at all). Review write-back commits this row in the
  // SAME transaction as the page write and, for an exactly-at-cap swap, the one
  // victim deletion. `output.evicted` identifies that victim when a swap lands.
  // Audit-chain conflicts retry the whole transaction, never just the append.
  // The review loop runs on the BYOVM, so there is no mothership `agent_sessions`
  // row to parent it to; the ACCOUNT's gateway session is the durable anchor,
  // not the parent-less stdout job-audit store.
  //   Three writers, two visibilities. The worker's two, the review write-back above
  //   (row built in `apps/worker/src/wiring/kb-page-store.ts`) and the initial import's
  //   seeding (`apps/worker/src/consumers/kb-import.ts`), stamp `visibleToMaintainer: false`.
  //   The write-back's row can also record a DELETION (#6649): a per-PR page the cap
  //   refused that landed by evicting one folded per-PR page carries `output.evicted`
  //   `{ slug, foldedAt }`, appended on the eviction's own transaction
  //   (`apps/worker/src/wiring/kb-page-store.ts`) — the only record of that delete.
  //   The api's REST door (`apps/api/src/routes/kb.ts`, #6124: a person or a token
  //   replaced a page) stamps `true`; its rows before #6124 are `mcp_tool.call.ok`
  //   naming this value in `tool`. The Terminal renderer
  //   (`../terminal/renderers/review-knowledge.ts`) shows the visible rows and returns
  //   null on the worker's withheld ones.
  KbPageUpserted: 'kb.page_upserted',
  // #2041 / the same lane: the mothership KB consumer was REFUSED a write by
  // `kb.background.max_pages`. New per-PR pages may swap one eligible folded page
  // at exactly cap; a refusal is not a permanent ban on new slugs. The nightly
  // gardener separately makes headroom. Existing-slug rewrites do not grow the KB.
  // The row carries repo, slug, pages held, cap and policy key. Review refusals,
  // including `review/log`, carry `input.swap`: `not_per_pr`, `over_cap`,
  // `deletion_off`, `policy_unresolved`, `nothing_folded` or `raced`, explaining
  // why no swap landed. A refusal deletes no victim and emits no successful-write
  // row. Emitted through the durable account-anchored dispatch sink; successful
  // review writes instead commit their audit atomically with the mutation.
  KbPageCapped: 'kb.page_capped',
  // arc 101 slice 04: the initial KB importer settled WITHOUT seeding a page —
  // one row per no-op decision (`no_installation` / `already_seeded` / `no_key` /
  // `archived_or_fork` / `empty_repo` / `no_pages`) or a budget cap that dropped
  // model-proposed pages (`budget_capped`). The producer removes the job on
  // settle (`removeOnComplete`/`removeOnFail`), so this is the ONLY durable record
  // of WHY a repo was or wasn't seeded (audit is trust). Emitted at BOTH seams:
  // the producer's already-seeded gate (no job enqueued) and the consumer's own
  // gates. Parent-less, via the worker's structured job-audit store.
  KbImportSkipped: 'kb.import.skipped',
  // arc 101 slice 04: the initial KB importer THREW — the model `generate` call
  // or the output schema parse failed. Recorded BEFORE the throw propagates (the
  // retry lane re-attempts), so a failed seed is never invisible on settle.
  KbImportFailed: 'kb.import.failed',
  // arc 101 slice 12 step 5: the backlog import (on a repo's first enable, or an
  // operator re-run, #5716) walked a repo's OPEN issues and filed each as a
  // `raw_tasks` ask. One row per PASS, not per ask, and that is deliberate — the
  // number a maintainer needs is "how much of my tracker did you take on, and did you
  // stop early", which is a property of the walk. The row carries the scan count, what
  // was filed vs already present, the budget spent, and whether the walk hit its
  // bound, so an import that saw 400 issues and admitted 171 can never read as a repo
  // with 171 open issues. On the ACCOUNT's chain (the lane's tenant arm), with the
  // import's `jobId` so an operator's `backlog.import_requested` pairs with it.
  BacklogImported: 'backlog.imported',
  // arc 101 slice 12 step 5: the backlog import filed NOTHING because it could not
  // act — the repo was switched off or its installation was revoked between the
  // enqueue and the run, no live installation resolves at all, or the repo's policy
  // is not `handoff.mode: fleet` (the consent gate). Carries the `jobId`. Its own kind
  // rather than a zero-count {@link AuditEventKind.BacklogImported}: "we walked your
  // tracker and it was empty" and "we never looked" are different answers, and a
  // maintainer asking why their backlog is invisible needs them apart.
  BacklogImportSkipped: 'backlog.import_skipped',
  // #5716: a platform operator asked for the import again on an ALREADY-enabled repo
  // (`apps/api` `routes/admin-backlog-import.ts`) — the lane's only other producer is the
  // first enable, so a repo enabled before the lane shipped had no way in. On the repo's
  // ACCOUNT chain, naming the operator in `input.actor`, with the `jobId`; written BEFORE
  // the enqueue, and the pass then writes its own `backlog.imported` / `import_skipped`.
  // `_enqueue_failed`: that hand-off threw after the request row landed, so no pass follows.
  BacklogImportRequested: 'backlog.import_requested',
  BacklogImportEnqueueFailed: 'backlog.import_enqueue_failed',
  // #1669: the autonomous scout backlog-drain minted a scout planning task for
  // one escalated issue-session on a fleet-mode repo (the producer that closes
  // the idea→coding-task gap without a human at the dispatch seam). Parent-less
  // (the drain is a cron, not a session), via the worker's structured job-audit
  // store. One row per FRESH mint — a null `createTaskIfAbsent` is an idempotent
  // re-tick and audits nothing.
  ScoutBacklogDrained: 'scout_backlog.drained',
  // #6370: the same drain DECLINED one escalated issue-session for good — the author is
  // a project member under the repo's `triage.skip_project_members` — and recorded it in
  // `scout_drain_skips`, which takes the session out of every later drain. One row per
  // landed insert (a racing repeat lands none), on the session's account chain.
  ScoutBacklogDeclined: 'scout_backlog.declined',
  // #4093: the autonomous repo-survey pump minted a `return_context: true`
  // scout task for one enabled `handoff.mode: fleet` repo on the current sweep
  // bucket. Parent-less (the pump is a cron, not a session), via the worker's
  // structured job-audit store. One row per FRESH mint — a null
  // `createTaskIfAbsent` is an idempotent re-tick and audits nothing. The
  // survey writer (`#4090`) is downstream of this row: the scout task runs,
  // the writer files asks under `survey_filing.drafted`, and the reader
  // (`#4058`) admits them.
  RepoSurveyPumped: 'repo_survey.pumped',
  // #1222: per-call audit when an agent MCP verb resolves an integration
  // connection (`findIntegration`). Lifecycle events (`integration.connected` /
  // `integration.disconnected`) stay stdout-only structured belt logging at
  // `apps/api/src/deps/integrations.ts` — out of scope for this kind. This row
  // is the durable trail behind every credential use, parented on a
  // born-finished, account-anchored `agent_sessions` row (no repo) so it
  // surfaces through `/v1/audit/events` under the same tenant fence as the
  // rest of the account tail.
  IntegrationUsed: 'integration.used',
  // #1535: the BYOK provider-reconciliation cron found a key our own DB calls
  // `invalid` that the PROVIDER still honours — a provider rejection our own
  // classifier may have got wrong, which is a real finding. It is written about
  // `invalid` rows ONLY since #4286: a `revoked` row is a soft-disable of our
  // stored copy that never reached the provider, so "still authenticates" is its
  // expected state, and that arm was writing 1,584 rows a day about nothing —
  // 15.5% of one tenant's 24h audit chain.
  //
  //   THE PROPERTY THAT MADE THAT DELETION SAFE, with its enumeration — stated
  //   ONCE, here, because the two other sites that used to assert it
  //   (`cron/byok-provider-reconcile.ts`, `apps/worker/docs/`) named two FILES
  //   and nothing at all respectively. The 2026-08-09 ~$69 finding (three
  //   OpenRouter keys called `revoked` while live upstream) is closed by a
  //   DIFFERENT mechanism than this lane, and the property is: **no row that is
  //   not `valid` can be PICKED TO RUN INFERENCE.** Every reader that picks one
  //   gates on `status = 'valid'`, across two seams:
  //     - `validOrgKey` (`packages/db/src/queries/byok/predicates.ts`), the
  //       shared predicate of `findActiveKey`, `findKeyFor` and
  //       `findLatestVerifiedKeyForReporting` (`byok/selection.ts`);
  //     - `listAdmissionKeys` and `listPoolKeys` (`byok/reads.ts`), which each
  //       carry their OWN hand-copied inline conjunct rather than the shared
  //       one — the two no test reached until #4286 added one.
  //   The other side, because a gate list with no complement proves nothing.
  //   Every remaining reader of `byok_keys` is one of: a read that deliberately
  //   selects NON-valid rows and makes an AUTH-ONLY `GET` with them
  //   (`findTransportFlaggedKeysDue`, `byok/verification.ts`; and this very
  //   cron's probe) — never a completion, so nothing is spent; a sweep that
  //   gates on `valid` anyway but picks no inference (`findKeysWithStaleLimits`,
  //   `byok/limits.ts`, a free `/models` GET); or a by-id / management surface
  //   that is HANDED a key rather than selecting one (`findKeyByIdForOwner`,
  //   `findKeyByIdUnfenced`, `findByokKeyForAccount`, `listKeysByAccount`,
  //   `listPersonalKeys`), none of which gate on status and none of which choose
  //   what a session spends.
  //   Pinned against real Postgres, not asserted: `byok.integration.test.ts` ->
  //   'never POOLS or ADMITS a revoked or invalid key' covers the two
  //   hand-copied conjuncts plus the trio on the same rows, and the `conjunct`
  //   tests beside it cover each part of `validOrgKey` separately. Relax any one
  //   of those gates and that file goes red.
  //
  // Unlike the platform-global detection-only lanes beside it (`release.drift`,
  // `ga_not_ready`), this finding is ACCOUNT-scoped and money-relevant, so it
  // earns a durable row beyond the `level: 'alert'` structured log a pod
  // restart would otherwise take with it. The row records the disagreement AND
  // the ACTION taken (#4269): the cron restores an `invalid` row the provider
  // still honours, under `gradeRestore`'s flap bound (a rejection within
  // `BYOK_RESTORE_FLAP_WINDOW_MS` of the last proof of liveness is held, not
  // restored), and pairs each restore with an `account.resumed` row — the mirror
  // of the `account.paused reason=byok_invalid` the invalidation wrote. It still
  // never revokes upstream (that acts on a credential we do not own) and never
  // restores a `revoked` row — since #4286 it never even enumerates one, so a
  // deliberate retirement is not a verdict the lane reaches and refuses, it is a
  // row the lane is never handed.
  // ACCOUNT-anchored, and
  // that is what makes the durable half above true: `cron.byok_provider_reconcile`
  // declares the `tenant` audit arm (`cron/byok-provider-reconcile.lane.ts`,
  // `apps/worker`), the sink owns the chain anchor, and the row lands in the
  // audit view of the account whose credential it is about. The "structured
  // job-audit store" this comment named until now IS the pod log, so it could
  // never have been the durable record the sentence above it promises.
  ByokProviderDisagreement: 'byok.provider_disagreement',
  // #2022: the fleet `git_token` RPC — the highest-value thing the platform hands
  // out. A box asks on `fleet.req.<runnerId>` and, when its CLAIM covers the repo,
  // receives a real GitHub App installation token for a customer's repository.
  // Both halves earn a row: the MINT, because a credential's birth is the first
  // thing an incident review looks for (the `token.created` argument, one
  // credential over), and the REFUSAL, because the wire deliberately collapses
  // `no_claim` / `not_your_repo` / `unknown_repo` into one answer so a box cannot
  // use the verb as a cross-tenant repo-existence oracle — the DISTINCT reason has
  // nowhere else to live.
  //   Until now both went to `deps.log` (a `console.log`) and nowhere else, so
  //   `audit_events WHERE kind LIKE 'git_token%'` returned ZERO rows. #2015 was a
  //   live cross-tenant hole on exactly this verb — any box could mint a working
  //   token for any other tenant's private repo — and "did anyone actually exploit
  //   it" had to be answered INDIRECTLY, from task/repo ownership, because
  //   container logs roll and are not queryable per account. A decent proxy is not
  //   a record.
  //   The row carries the runner, the account, the repo, the installation and the
  //   expiry. **Never the token**: it exists only in the worker process for the
  //   NATS round-trip and on the box for the run.
  //   Anchored on the CLAIM's account (`ensureGatewaySessionParent`); a refusal
  //   decidable before any claim resolves (`no_claim`, an unconfigured pod, a DB
  //   fault) has no tenant to anchor on and stays on the structured-log belt, the
  //   same deliberate exception the babysit repo stand-down takes.
  FleetGitTokenMinted: 'fleet.git_token.minted',
  FleetGitTokenRefused: 'fleet.git_token.refused',
  // A LIVE run re-read its repo's vault over `fleet.req` `repo_vault` (arc 101
  // slice 03, onboarding mode) — the claim-frozen `assignment.vault` re-resolved and
  // re-sealed to the box that holds the task, so a secret the maintainer stored
  // mid-run reaches it. The sibling of the git_token pair: credential material
  // handed to a box, so every hand-over is a row. ONE row per SERVED request,
  // written BEFORE the entries leave (no row, no entries — `presign-rpc.ts`
  // `handleRepoVaultRequest`). It carries the task, the box and the entry COUNT:
  // never a secret name, a value or a sealed blob. Maintainer-visible — these are
  // their secrets reaching a box that runs their work.
  VaultReread: 'vault.reread',
  // The mothership ENDED a review check run on a pull request it did not own
  // (#2327 follow-up). A `queued` check with no conclusion blocks a merge loop, so
  // the platform bounds its own check's lifetime and concludes it `neutral` when the
  // budget runs out. That is a public, machine-read statement about someone else's
  // pull request made by nobody's request, which is exactly the class of act that
  // earns a row: the log line a pod restart takes with it cannot answer "why does
  // this PR say the review did not run".
  //
  // Maintainer-visible — the sentence is already on their pull request, and the fix
  // (a box that can serve the review) is on our side but the consequence is theirs.
  // ONE row per APPLIED settle: a stand-down on a run somebody already concluded
  // wrote nothing and must not claim to have.
  ReviewCheckSettled: 'review.check_settled',
  // The watchdog observed current ownership before changing a check or retry obligation.
  ReviewCheckObserved: 'review.check_observed',
  // The fleet alert lane has a three-way answer at its emit seam
  // (`createDiscordChannel.deliver`): `{delivered:true}` posts the message and the
  // row here records it; `{delivered:false}` means the webhook is unconfigured
  // or the channel is OFF, which is an OPERATOR decision and must be VISIBLE in
  // audit; a thrown transport (4xx, 429 / DiscordRateLimited, network error) is
  // a REAL FAILURE with a real stack. Three kinds, not one with an outcome
  // field: `kind` is the indexed query axis, and an operator asking "has the
  // alert lane delivered anything this week" needs `WHERE kind =`, not payload
  // parsing.
  FleetAlertDelivered: 'fleet_alert.delivered',
  FleetAlertSuppressed: 'fleet_alert.suppressed',
  FleetAlertFailed: 'fleet_alert.failed',
  // #3158: the review KB consumer could NOT credit the pages a review cited
  // (`kb:<slug>` in its findings -> `markRepoMemoryPagesUsed`). The bump is the
  // fleet's only usage signal for `repo_memory.used_count`, which drives the
  // gardener's review-page TTL and the agent-tuner's "never retrieved" finding —
  // so a failed credit changes what gets EVICTED and what gets FILED, and the
  // consumer deliberately swallows it (a rethrow would NAK an already-applied page
  // write into a double-bumping redelivery). Prod wiring supplies no logger, so
  // without this row the failure had no durable trace. Carries the run, task,
  // repo, the page written, the cited slugs and the reason. Emitted through the
  // durable dispatch sink, account-anchored like its `kb.page_*` siblings.
  KbCitationsUncredited: 'kb.citations_uncredited',
} as const;
